// node_modules
import { SavedQueryControllerSingleton } from "Controllers";
import { SynonymTypeEnum } from "Enums";
import {
    createContext,
    useCallback,
    useState
} from "react";
// Types
import { TSavedQueryTempDTO, TActionObjectDTO, TEnvironmentVariableDTO, TSpecificationUnitDTO, TSynonymDTO } from "Types";

type TSavedQueryContext = {
    savedQuery: TSavedQueryTempDTO | undefined,
    actionObjects: TActionObjectDTO[],
    environmentVariables: TEnvironmentVariableDTO[],
    specificationUnits: TSpecificationUnitDTO[],
    updateSavedQuery: (newSavedQuery: TSavedQueryTempDTO | undefined) => void,
    duplicateSavedQuery: (selectedOriginalTechnologyId: number | undefined, selectedCaseId: number | undefined, callback: (duplicatedSavedQuery: TSavedQueryTempDTO) => Promise<void>) => Promise<void>,
    updateSynonyms: (synonymType: SynonymTypeEnum, synonymsParent: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO,
        newSynonymList: TSynonymDTO[], savedQueryToUseForUpdate?: TSavedQueryTempDTO | undefined) => void,
    isSavedQueriesLoading: boolean | undefined,
    updateIsSavedQueriesLoading: (isLoading: boolean) => void
}

export const SavedQueryContext = createContext<TSavedQueryContext>({
    savedQuery: undefined,
    isSavedQueriesLoading: undefined,
    updateIsSavedQueriesLoading: () => { },
    actionObjects: [],
    environmentVariables: [],
    specificationUnits: [],
    updateSavedQuery: (newSavedQuery: TSavedQueryTempDTO | undefined) => { },
    duplicateSavedQuery: async (selectedOriginalTechnologyId: number | undefined, selectedCaseId: number | undefined, callback: (duplicatedSavedQuery: TSavedQueryTempDTO) => Promise<void>) => { },
    updateSynonyms: (synonymType: SynonymTypeEnum, synonymsParent: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO,
        newSynonymList: TSynonymDTO[], savedQueryToUseForUpdate?: TSavedQueryTempDTO | undefined) => { }
});

export const SavedQueryProvider = ({ children }: any) => {
    const [savedQuery, setSavedQuery] = useState<TSavedQueryTempDTO | undefined>(undefined);
    const [isSavedQueriesLoading, setIsSavedQueriesLoading] = useState<boolean | undefined>(undefined);
    const [actionObjects, setActionObjects] = useState<TActionObjectDTO[]>([]);
    const [environmentVariables, setEnvironmentVariables] = useState<TEnvironmentVariableDTO[]>([]);
    const [specificationUnits, setSpecificationUnits] = useState<TSpecificationUnitDTO[]>([]);

    const updateIsSavedQueriesLoading = useCallback((newIsSavedQueriesLoading: boolean) => {
        setIsSavedQueriesLoading(newIsSavedQueriesLoading);
    }, [setIsSavedQueriesLoading]);

    const updateSavedQuery = useCallback((newSavedQuery: TSavedQueryTempDTO | undefined) => {
        if (!newSavedQuery) {
            setSavedQuery(undefined);
            setActionObjects([]);
            setEnvironmentVariables([]);
            setSpecificationUnits([]);
        } else {
            setSavedQuery(newSavedQuery);
            setActionObjects([...newSavedQuery.actionObjects]);
            setEnvironmentVariables([...newSavedQuery.environmentVariables]);
            setSpecificationUnits([...newSavedQuery.specificationUnits]);
        }
    }, [setSavedQuery]);

    const updateSynonyms = useCallback((synonymType: SynonymTypeEnum, synonymsParent: TActionObjectDTO | TEnvironmentVariableDTO | TSpecificationUnitDTO,
        newSynonymList: TSynonymDTO[], savedQueryToUseForUpdate?: TSavedQueryTempDTO | undefined): void => {
        // safety-checks
        if (!savedQuery) return;
        if (!savedQueryToUseForUpdate) savedQueryToUseForUpdate = savedQuery;

        switch (synonymType) {
            case SynonymTypeEnum.Action:
                for (const actionObject of savedQueryToUseForUpdate.actionObjects) {
                    if (actionObject.id === (synonymsParent as TActionObjectDTO).id) {
                        const newSynonyms: TSynonymDTO[] = [];
                        for (const synonym of newSynonymList) {
                            const synonymAlreadyAdded: TSynonymDTO[] = newSynonyms.filter(newSynonym => newSynonym.text.trim().toLowerCase() === synonym.text.trim().toLowerCase());
                            if (!synonymAlreadyAdded || synonymAlreadyAdded.length < 1) {
                                newSynonyms.push(synonym);
                            }
                        }
                        actionObject.actionSynonyms = [...newSynonyms];
                        break;
                    }
                }
                // update action object list in the current context
                updateSavedQuery(savedQueryToUseForUpdate);
                break;
            case SynonymTypeEnum.Object:
                for (const actionObject of savedQueryToUseForUpdate.actionObjects) {
                    if (actionObject.id === (synonymsParent as TActionObjectDTO).id) {
                        const newSynonyms: TSynonymDTO[] = [];
                        for (const synonym of newSynonymList) {
                            const synonymAlreadyAdded: TSynonymDTO[] = newSynonyms.filter(newSynonym => newSynonym.text.trim().toLowerCase() === synonym.text.trim().toLowerCase());
                            if (!synonymAlreadyAdded || synonymAlreadyAdded.length < 1) {
                                newSynonyms.push(synonym);
                            }
                        }
                        actionObject.objectSynonyms = [...newSynonyms];
                        break;
                    }
                }
                // update action object list in the current context
                updateSavedQuery(savedQueryToUseForUpdate);
                break;
            case SynonymTypeEnum.Environment:
                for (const environmentVariable of savedQueryToUseForUpdate.environmentVariables) {
                    if (environmentVariable.id === (synonymsParent as TEnvironmentVariableDTO).id) {
                        const newSynonyms: TSynonymDTO[] = [];
                        for (const synonym of newSynonymList) {
                            const synonymAlreadyAdded: TSynonymDTO[] = newSynonyms.filter(newSynonym => newSynonym.text.trim().toLowerCase() === synonym.text.trim().toLowerCase());
                            if (!synonymAlreadyAdded || synonymAlreadyAdded.length < 1) {
                                newSynonyms.push(synonym);
                            }
                        }
                        environmentVariable.synonyms = [...newSynonyms];
                        break;
                    }
                }
                // update environment variable list in the current context
                updateSavedQuery(savedQueryToUseForUpdate);
                break;
            case SynonymTypeEnum.Unit:
                for (const specificationUnit of savedQueryToUseForUpdate.specificationUnits) {
                    if (specificationUnit.id === (synonymsParent as TSpecificationUnitDTO).id) {
                        const newSynonyms: TSynonymDTO[] = [];
                        for (const synonym of newSynonymList) {
                            const synonymAlreadyAdded: TSynonymDTO[] = newSynonyms.filter(newSynonym => newSynonym.text.trim().toLowerCase() === synonym.text.trim().toLowerCase());
                            if (!synonymAlreadyAdded || synonymAlreadyAdded.length < 1) {
                                newSynonyms.push(synonym);
                            }
                        }
                        specificationUnit.unitSynonyms = [...newSynonyms];
                        break;
                    }
                }
                updateSavedQuery(savedQueryToUseForUpdate);
                break;
            default:
                break;
        }
    }, [savedQuery, updateSavedQuery]);

    const duplicateSavedQuery = useCallback(async (
            selectedOriginalTechnologyId: number | undefined,
            selectedCaseId: number | undefined,
            callback: (duplicatedSavedQuery: TSavedQueryTempDTO) => Promise<void>): Promise<void> => {
        if (!savedQuery || !selectedOriginalTechnologyId) return;

        const duplicatedSavedQuery = await SavedQueryControllerSingleton.saveFirstDeliveryQueryChange(savedQuery,
            selectedOriginalTechnologyId === -1 ? "case" : "technology",
            selectedOriginalTechnologyId === -1 ? selectedCaseId! : selectedOriginalTechnologyId);

        updateSavedQuery(duplicatedSavedQuery);

        await callback(duplicatedSavedQuery);
    }, [savedQuery, updateSavedQuery]);

    return (
        <SavedQueryContext.Provider
            value={{
                savedQuery,
                actionObjects,
                environmentVariables,
                specificationUnits,
                updateSavedQuery,
                duplicateSavedQuery,
                updateSynonyms,
                isSavedQueriesLoading,
                updateIsSavedQueriesLoading
            }}>
            {children}
        </SavedQueryContext.Provider>
    );
};